import { parseUrl } from './parse-url';

export enum TextWithUrlsSegmentKind {
  Text = 'text',
  Url = 'url',
}

export type TextSegment = {
  kind: TextWithUrlsSegmentKind.Text;
  value: string;
};

// also carries phone numbers — their `tel:` href is a URI like any other, and consumers
// render every one of these the same way: as a link
export type UrlSegment = {
  kind: TextWithUrlsSegmentKind.Url;
  value: string;
  href: string;
};

export type TextWithUrlsSegment = TextSegment | UrlSegment;

// The bare-domain label run is capped: uncapped it backtracks O(n²) on text like "a.a.a.…".
const urlRegex =
  // eslint-disable-next-line max-len -- regex needs this length
  /\b[a-z][a-z0-9+.-]*:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]|\bwww\.[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|\b(?=((?:[A-Z0-9-]+\.){1,10}))\1[A-Z]{2,}\b|\bmailto:[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]/gi;

const MIN_PHONE_DIGITS = 7;
const MAX_PHONE_DIGITS = 15;

// Anchored on `+` (international) or `0` (national trunk prefix). A bare digit run is far
// more often an invoice number, an order id or a year than something worth dialling, so it
// deliberately does not match.
const phoneRegex = /[+0]\d[\d\s().-]{4,}\d/g;

// dates wear the same digits-and-separators shape: 2026-07-29, 09-07-2026, 06.07.2026
const dateLikeRegex = /^\d{1,4}[-.]\d{1,2}[-.]\d{1,4}$/;

const countDigits = (value: string) => (value.match(/\d/g) ?? []).length;

const isPhoneNumber = (value: string) => {
  if (dateLikeRegex.test(value)) {
    return false;
  }
  const digits = countDigits(value);
  return digits >= MIN_PHONE_DIGITS && digits <= MAX_PHONE_DIGITS;
};

const toTelHref = (value: string) => `tel:${value.replace(/[\s().-]/g, '')}`;

const splitPhoneNumbers = (value: string): TextWithUrlsSegment[] => {
  const matches = [...value.matchAll(phoneRegex)].filter((match) =>
    isPhoneNumber(match[0]),
  );

  if (!matches.length) {
    return [{ kind: TextWithUrlsSegmentKind.Text, value }];
  }

  const segments: TextWithUrlsSegment[] = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      segments.push({
        kind: TextWithUrlsSegmentKind.Text,
        value: value.substring(lastIndex, matchIndex),
      });
    }

    const phone = match[0];
    segments.push({
      kind: TextWithUrlsSegmentKind.Url,
      value: phone,
      href: toTelHref(phone),
    });

    lastIndex = matchIndex + phone.length;
  });

  if (lastIndex < value.length) {
    segments.push({
      kind: TextWithUrlsSegmentKind.Text,
      value: value.substring(lastIndex),
    });
  }

  return segments;
};

export const splitTextWithUrls = (value: string): TextWithUrlsSegment[] => {
  if (!value) {
    return [];
  }

  const matches = [...value.matchAll(urlRegex)];
  const segments: TextWithUrlsSegment[] = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      segments.push({
        kind: TextWithUrlsSegmentKind.Text,
        value: value.substring(lastIndex, matchIndex),
      });
    }

    const url = match[0];
    segments.push({
      kind: TextWithUrlsSegmentKind.Url,
      value: url,
      href: parseUrl(url),
    });

    lastIndex = matchIndex + url.length;
  });

  if (lastIndex < value.length) {
    segments.push({
      kind: TextWithUrlsSegmentKind.Text,
      value: value.substring(lastIndex),
    });
  }

  // phones are matched in what the URL pass left over, so digits inside a URL stay in it
  return segments.flatMap((segment) =>
    segment.kind === TextWithUrlsSegmentKind.Text
      ? splitPhoneNumbers(segment.value)
      : segment,
  );
};
